import { useEffect, useState, useCallback } from 'react';
import { BigNumber } from 'ethers';
import {
  TectonicAsset,
  PartnerTokenRate,
} from '@cronos-labs/tectonic-sdk/dist/types';

import { notify } from '@lib/bugsnag';
import useAppDispatch from '@hooks/useAppDispatch';
import { getAssetBorrowAmount, refreshAssetBorrowAmount } from '@redux/markets';
import {
  getAssetBorrowAmount as getAssetBorrowAmountDeFiPool,
  refreshAssetBorrowAmount as refreshAssetBorrowAmountDeFiPool,
} from '@redux/defiMarkets';
import {
  getAssetBorrowAmount as getAssetBorrowAmountVenoPool,
  refreshAssetBorrowAmount as refreshAssetBorrowAmountVenoPool,
} from '@redux/venoMarkets';
import { Mode } from '@components/MarketsPageView/types';
import useSdkAndSupportedAssets from '@hooks/useSdkAndSupportedAssets';

interface BorrowCardState {
  apy: number | null;
  dailyDistributionAmount: BigNumber | null;
  nonTonicBorrowDailyDistributeAmount: PartnerTokenRate[];
  loadingApy: boolean;
  loadingDailyDistributionAmount: boolean;
  loadingNonTonicBorrowDailyDistributeAmount: boolean;
}

const initState: BorrowCardState = {
  apy: null,
  dailyDistributionAmount: null,
  nonTonicBorrowDailyDistributeAmount: [],
  loadingApy: true,
  loadingDailyDistributionAmount: true,
  loadingNonTonicBorrowDailyDistributeAmount: true,
};

export interface UseBorrowCardDataResult {
  data: {
    apy: BorrowCardState['apy'];
    dailyDistributionAmount: BorrowCardState['dailyDistributionAmount'];
    nonTonicBorrowDailyDistributeAmount: BorrowCardState['nonTonicBorrowDailyDistributeAmount'];
  };
  loading: boolean;
  refetch: () => void;
}

function useBorrowCardData(
  asset: TectonicAsset | null,
  mode: Mode
): UseBorrowCardDataResult {
  const dispatch = useAppDispatch();
  const { sdk } = useSdkAndSupportedAssets(mode);
  const [data, setData] = useState<BorrowCardState>(initState);

  const getData = useCallback(
    async (a: TectonicAsset, isRefetching?: boolean): Promise<void> => {
      function getBorrowAmount(): void {
        if (isRefetching) {
          switch (mode) {
            case 'VENO':
              dispatch(refreshAssetBorrowAmountVenoPool(sdk, a));
              break;
            case 'DEFI':
              dispatch(refreshAssetBorrowAmountDeFiPool(sdk, a));
              break;
            case 'MAIN':
            default:
              dispatch(refreshAssetBorrowAmount(sdk, a));
          }
        } else {
          switch (mode) {
            case 'VENO':
              dispatch(getAssetBorrowAmountVenoPool(sdk, a));
              break;
            case 'DEFI':
              dispatch(getAssetBorrowAmountDeFiPool(sdk, a));
              break;
            case 'MAIN':
            default:
              dispatch(getAssetBorrowAmount(sdk, a));
          }
        }
      }

      const getAssetBorrowApyAndDistribution = async (): Promise<void> => {
        try {
          const [
            apy,
            dailyDistributionAmount,
            nonTonicBorrowDailyDistributeAmount,
          ] = await Promise.all([
            sdk.Borrow.borrowApy(a.tTokenAddress),
            sdk.Tonic.tonicDailyDistributeRateForBorrow(a.tTokenAddress),
            mode === 'DEFI' // || 'VENO'
              ? sdk.Tonic.nonTonicDailyDistributeRateForSupply(a.tTokenAddress)
              : [],
          ]);

          setData((v) => ({
            ...v,
            apy,
            dailyDistributionAmount,
            nonTonicBorrowDailyDistributeAmount,
            loadingApy: false,
            loadingDailyDistributionAmount: false,
            loadingNonTonicBorrowDailyDistributeAmount: false,
          }));
        } catch (error) {
          notify(error);
          setData((v) => ({
            ...v,
            loadingApy: false,
            loadingDailyDistributionAmount: false,
            loadingNonTonicBorrowDailyDistributeAmount: false,
          }));
        }
      };

      getBorrowAmount();
      getAssetBorrowApyAndDistribution();
    },
    [dispatch, sdk, mode]
  );

  const refetch = useCallback(() => {
    if (asset) {
      getData(asset, true);
    }
  }, [asset, getData]);

  useEffect(() => {
    if (asset) {
      getData(asset);
    }
  }, [asset, getData]);

  return {
    data: {
      apy: data.apy,
      dailyDistributionAmount: data.dailyDistributionAmount,
      nonTonicBorrowDailyDistributeAmount:
        data.nonTonicBorrowDailyDistributeAmount,
    },
    loading:
      data.loadingApy ||
      data.loadingDailyDistributionAmount ||
      data.loadingNonTonicBorrowDailyDistributeAmount,
    refetch,
  };
}

export default useBorrowCardData;
